import React from 'react'
import { Link, useLocation } from 'react-router-dom'
import { observer } from 'mobx-react-lite'
import { PanelLeftFilledIcon, PanelLeftIcon } from '@components/AdminMenuIcons'
import type { OperatorPresenceStatus } from '@utils/operatorPresence'
import { useStore } from '@store/StoreProvider'
import { ADMIN_NAV_I18N_KEYS } from '@constants/adminNav'

type NavMeta = {
  icon?: React.ReactNode
  badge?: string
  unread?: boolean
  billingAlert?: 'warning' | 'critical'
}

type SidebarNavProps = {
  items: string[]
  active: string
  className?: string
  routes?: Record<string, string>
  onItemClick?: (item: string) => void
  collapsible?: boolean
  collapsed?: boolean
  onToggleCollapse?: (next: boolean) => void
  navMeta?: Record<string, NavMeta>
  showStatusToggle?: boolean
  isOnLunch?: boolean
  onLunchToggle?: (next: boolean) => void
  /** Blocks the lunch/online toggle while a presence API request is in flight */
  statusSwitchDisabled?: boolean
  presenceStatus?: OperatorPresenceStatus | null
  breakStartedAt?: string | null
  breakUntil?: string | null
  breakReason?: string | null
}

function formatBreakTime(iso: string, locale: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const loc = locale === 'en' ? 'en-US' : locale === 'ua' ? 'uk-UA' : locale === 'kz' ? 'kk-KZ' : 'ru-RU'
  return d.toLocaleTimeString(loc, { hour: '2-digit', minute: '2-digit' })
}

function buildStatusCopy(
  isOnLunch: boolean,
  presenceStatus: OperatorPresenceStatus | null | undefined,
  breakReason: string | null | undefined,
  breakStartedAt: string | null | undefined,
  breakUntil: string | null | undefined,
  t: (key: string) => string,
  locale: string,
): { label: string; title: string; hint: string } {
  if (presenceStatus === 'offline') {
    return {
      label: t('app.status.offline'),
      title: t('app.status.not_online'),
      hint: t('chat.lunch.unavailable'),
    }
  }
  if (isOnLunch || presenceStatus === 'lunch') {
    const parts: string[] = [t('chat.lunch.colleagues_see_unavailable')]
    if (breakReason) parts.push(breakReason)
    const started = breakStartedAt ? formatBreakTime(breakStartedAt, locale) : ''
    const until = breakUntil ? formatBreakTime(breakUntil, locale) : ''
    if (started && until) parts.push(`${started}–${until}`)
    else if (started) parts.push(started)
    else if (until) parts.push(until)
    return {
      label: t('chat.lunch.status'),
      title: t('chat.lunch.mode'),
      hint: parts.join(' · '),
    }
  }
  return {
    label: t('app.status.active'),
    title: t('chat.presence.online'),
    hint: t('chat.status.online_hint'),
  }
}

const DEFAULT_META: Record<string, NavMeta> = {
  'Мой профиль': { icon: profileIcon() },
  Диалоги: { icon: dialogsIcon() },
  Команда: { icon: teamIcon() },
  Посетители: { icon: visitorsIcon() },
  Статистика: { icon: statsIcon() },
  Биллинг: { icon: billingIcon() },
  Управление: { icon: settingsIcon() },
  Настройки: { icon: preferencesIcon() },
}

const SidebarNav: React.FC<SidebarNavProps> = observer(({
  items,
  active,
  className = '',
  routes,
  onItemClick,
  collapsible = false,
  collapsed = false,
  onToggleCollapse,
  navMeta,
  showStatusToggle = false,
  isOnLunch = false,
  onLunchToggle,
  statusSwitchDisabled = false,
  presenceStatus = null,
  breakStartedAt = null,
  breakUntil = null,
  breakReason = null,
}) => {
  const location = useLocation()
  const { i18n } = useStore()
  const t = (key: string) => i18n.t(key)
  const navLabel = (item: string) => t(ADMIN_NAV_I18N_KEYS[item] || item)
  const meta: Record<string, NavMeta> = { ...DEFAULT_META }
  if (navMeta) {
    for (const [key, value] of Object.entries(navMeta)) {
      meta[key] = { ...DEFAULT_META[key], ...value }
    }
  }
  const statusSwitchRef = React.useRef<HTMLLabelElement | null>(null)
  const [statusTipAbove, setStatusTipAbove] = React.useState(false)

  const updateStatusTooltipPlacement = React.useCallback(() => {
    const root = statusSwitchRef.current
    if (!root) return
    const tip = root.querySelector('.sb-tooltip-text') as HTMLElement | null
    if (!tip) return

    const ar = root.getBoundingClientRect()
    const tipH = Math.max(tip.offsetHeight, tip.getBoundingClientRect().height, 32)
    const gap = 8
    const margin = 8
    const vh = window.innerHeight

    const fitsBelow = ar.bottom + gap + tipH <= vh - margin
    const fitsAbove = ar.top - gap - tipH >= margin

    let above = false
    if (fitsBelow) {
      above = false
    } else if (fitsAbove) {
      above = true
    } else {
      const spaceBelow = vh - ar.bottom
      const spaceAbove = ar.top
      above = spaceAbove > spaceBelow
    }
    setStatusTipAbove(above)
  }, [])

  React.useEffect(() => {
    if (!showStatusToggle) return
    const onResize = () => {
      const root = statusSwitchRef.current
      if (!root) return
      if (root.matches(':hover') || root.contains(document.activeElement)) {
        updateStatusTooltipPlacement()
      }
    }
    window.addEventListener('resize', onResize)
    return () => window.removeEventListener('resize', onResize)
  }, [showStatusToggle, updateStatusTooltipPlacement])

  const getRoute = (item: string): string => {
    if (routes && routes[item]) {
      return routes[item]
    }
    const defaultRoutes: Record<string, string> = {
      Диалоги: '/dialogs',
      Команда: '/team',
      Посетители: '/visitors',
      Статистика: '/statistics',
      Биллинг: '/billing',
      Управление: '/control',
      Настройки: '/settings',
    }
    return defaultRoutes[item] || '#'
  }

  const getActiveItem = (item: string): boolean => {
    if (active) {
      return item === active
    }
    const route = getRoute(item)
    return location.pathname === route
  }

  const { label: lunchLabel, title: lunchTitle, hint: lunchHint } = buildStatusCopy(
    isOnLunch,
    presenceStatus,
    breakReason,
    breakStartedAt,
    breakUntil,
    t,
    i18n.language,
  )

  return (
    <aside
      className={`sidebar ${className}`.trim()}
      data-collapsed={collapsed ? 'true' : 'false'}
      style={{
        transition: 'width var(--transition, .25s cubic-bezier(.2,.7,.2,1)), padding var(--transition, .25s cubic-bezier(.2,.7,.2,1))',
      }}
    >
      {collapsible && (
        <button
          type="button"
          className="sb-toggle cursor-pointer"
          onClick={() => onToggleCollapse?.(!collapsed)}
          aria-label={collapsed ? t('nav.expand_menu') : t('nav.collapse_menu')}
          aria-expanded={!collapsed}
        >
          {collapsed ? (
            <PanelLeftIcon size={18} />
          ) : (
            <PanelLeftFilledIcon size={18} />
          )}
        </button>
      )}

      <nav className="sb-nav">
        {items.map((item) => {
          const isActive = getActiveItem(item)
          const route = getRoute(item)
          const info = meta[item] || {}
          return (
            <Link
              key={item}
              to={route}
              className={`sb-link sb-with-tooltip ${isActive ? 'active' : ''}`}
              data-active={isActive}
              data-unread={info.unread ? 'true' : undefined}
              data-billing-alert={info.billingAlert || undefined}
              aria-current={isActive ? 'page' : undefined}
              onClick={() => onItemClick?.(item)}
            >
              <span className="sb-icon" aria-hidden="true">
                {info.icon || <span className="dot" />}
              </span>
              <span className="sb-link-label">{navLabel(item)}</span>
              {info.badge && <span className="sb-badge">{info.badge}</span>}
              {info.unread && <span className="sb-unread" />}
              {info.billingAlert === 'warning' && (
                <span className="sb-billing-dot" aria-label={t('billing.subscription.ending')} />
              )}
              {info.billingAlert === 'critical' && (
                <span className="sb-billing-bang" aria-label={t('billing.subscription.expired')}>
                  !
                </span>
              )}
              {collapsed && (
                <span className="sb-tooltip-text" aria-hidden="true">
                  {navLabel(item)}
                </span>
              )}
            </Link>
          )
        })}
      </nav>

      {showStatusToggle && (
        <div className="sb-bottom">
          {!collapsed && (
            <div className="sb-status-copy">
              <strong>{lunchTitle}</strong>
              <small>{lunchHint}</small>
            </div>
          )}

          <label
            ref={statusSwitchRef}
            className={`status-switch sb-with-tooltip${statusTipAbove ? ' status-switch-tooltip--above' : ''}`}
            onMouseEnter={updateStatusTooltipPlacement}
            onFocusCapture={updateStatusTooltipPlacement}
          >
            <input
              type="checkbox"
              checked={isOnLunch}
              onChange={() => onLunchToggle?.(!isOnLunch)}
              disabled={statusSwitchDisabled}
              aria-checked={isOnLunch}
              aria-busy={statusSwitchDisabled}
              aria-label={t('chat.lunch.toggle')}
            />
            <span className="track">
              <span className="knob" />
            </span>
            <span className="status-label">
              <b>{lunchLabel}</b>
            </span>
            <span className="sb-tooltip-text" aria-hidden="true">
              {t('chat.status.toggle')}
            </span>
          </label>
        </div>
      )}
    </aside>
  )
})

function profileIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-person-circle"
      viewBox="0 0 16 16"
    >
      <path d="M11 6a3 3 0 1 1-6 0 3 3 0 0 1 6 0" />
      <path
        fill-rule="evenodd"
        d="M0 8a8 8 0 1 1 16 0A8 8 0 0 1 0 8m8-7a7 7 0 0 0-5.468 11.37C3.242 11.226 4.805 10 8 10s4.757 1.225 5.468 2.37A7 7 0 0 0 8 1"
      />
    </svg>
  )
}

function dialogsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-chat-dots"
      viewBox="0 0 16 16"
    >
      <path d="M5 8a1 1 0 1 1-2 0 1 1 0 0 1 2 0m4 0a1 1 0 1 1-2 0 1 1 0 0 1 2 0m3 1a1 1 0 1 0 0-2 1 1 0 0 0 0 2" />
      <path d="m2.165 15.803.02-.004c1.83-.363 2.948-.842 3.468-1.105A9 9 0 0 0 8 15c4.418 0 8-3.134 8-7s-3.582-7-8-7-8 3.134-8 7c0 1.76.743 3.37 1.97 4.6a10.4 10.4 0 0 1-.524 2.318l-.003.011a11 11 0 0 1-.244.637c-.079.186.074.394.273.362a22 22 0 0 0 .693-.125m.8-3.108a1 1 0 0 0-.287-.801C1.618 10.83 1 9.468 1 8c0-3.192 3.004-6 7-6s7 2.808 7 6-3.004 6-7 6a8 8 0 0 1-2.088-.272 1 1 0 0 0-.711.074c-.387.196-1.24.57-2.634.893a11 11 0 0 0 .398-2" />
    </svg>
  )
}

function teamIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-people"
      viewBox="0 0 16 16"
    >
      <path d="M15 14s1 0 1-1-1-4-5-4-5 3-5 4 1 1 1 1zm-7.978-1L7 12.996c.001-.264.167-1.03.76-1.72C8.312 10.629 9.282 10 11 10c1.717 0 2.687.63 3.24 1.276.593.69.758 1.457.76 1.72l-.008.002-.014.002zM11 7a2 2 0 1 0 0-4 2 2 0 0 0 0 4m3-2a3 3 0 1 1-6 0 3 3 0 0 1 6 0M6.936 9.28a6 6 0 0 0-1.23-.247A7 7 0 0 0 5 9c-4 0-5 3-5 4q0 1 1 1h4.216A2.24 2.24 0 0 1 5 13c0-1.01.377-2.042 1.09-2.904.243-.294.526-.569.846-.816M4.92 10A5.5 5.5 0 0 0 4 13H1c0-.26.164-1.03.76-1.724.545-.636 1.492-1.256 3.16-1.275ZM1.5 5.5a3 3 0 1 1 6 0 3 3 0 0 1-6 0m3-2a2 2 0 1 0 0 4 2 2 0 0 0 0-4" />
    </svg>
  )
}

function visitorsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-person-lines-fill"
      viewBox="0 0 16 16"
    >
      <path d="M6 8a3 3 0 1 0 0-6 3 3 0 0 0 0 6m-5 6s-1 0-1-1 1-4 6-4 6 3 6 4-1 1-1 1zM11 3.5a.5.5 0 0 1 .5-.5h4a.5.5 0 0 1 0 1h-4a.5.5 0 0 1-.5-.5m.5 2.5a.5.5 0 0 0 0 1h4a.5.5 0 0 0 0-1zm2 3a.5.5 0 0 0 0 1h2a.5.5 0 0 0 0-1zm0 3a.5.5 0 0 0 0 1h2a.5.5 0 0 0 0-1z" />
    </svg>
  )
}

function statsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-graph-up"
      viewBox="0 0 16 16"
    >
      <path
        fill-rule="evenodd"
        d="M0 0h1v15h15v1H0zm14.817 3.113a.5.5 0 0 1 .07.704l-4.5 5.5a.5.5 0 0 1-.74.037L7.06 6.767l-3.656 5.027a.5.5 0 0 1-.808-.588l4-5.5a.5.5 0 0 1 .758-.06l2.609 2.61 4.15-5.073a.5.5 0 0 1 .704-.07"
      />
    </svg>
  )
}

function billingIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-wallet2"
      viewBox="0 0 16 16"
    >
      <path d="M12.136.326A1.5 1.5 0 0 1 14 1.78V3h.5A1.5 1.5 0 0 1 16 4.5v9a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 0 13.5v-9a1.5 1.5 0 0 1 1.432-1.499zM5.562 3H13V1.78a.5.5 0 0 0-.621-.484zM1.5 4a.5.5 0 0 0-.5.5v9a.5.5 0 0 0 .5.5h13a.5.5 0 0 0 .5-.5v-9a.5.5 0 0 0-.5-.5z" />
    </svg>
  )
}

function settingsIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      fill="currentColor"
      className="bi bi-sliders"
      viewBox="0 0 16 16"
    >
      <path
        fill-rule="evenodd"
        d="M11.5 2a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M9.05 3a2.5 2.5 0 0 1 4.9 0H16v1h-2.05a2.5 2.5 0 0 1-4.9 0H0V3zM4.5 7a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3M2.05 8a2.5 2.5 0 0 1 4.9 0H16v1H6.95a2.5 2.5 0 0 1-4.9 0H0V8zm9.45 4a1.5 1.5 0 1 0 0 3 1.5 1.5 0 0 0 0-3m-2.45 1a2.5 2.5 0 0 1 4.9 0H16v1h-2.05a2.5 2.5 0 0 1-4.9 0H0v-1z"
      />
    </svg>
  )
}

function preferencesIcon() {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      className="bi bi-gears"
    >
      <circle cx="16.5" cy="7.5" r="2" />
      <path d="M16.5 3.9v1.1M16.5 10v1.1M13.95 5.35l.78.78M18.27 9.67l.78.78M12.9 7.5H14M19 7.5h1.1M13.95 9.65l.78-.78M18.27 5.33l.78-.78" />
      <circle cx="8.25" cy="15.25" r="3.1" />
      <path d="M8.25 10.85v1.3M8.25 18.35v1.3M4.95 12.05l.92.92M10.63 17.73l.92.92M3.75 15.25h1.3M11.45 15.25h1.3M4.95 18.45l.92-.92M10.63 12.77l.92-.92" />
    </svg>
  )
}

export default SidebarNav


